import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff, FileText, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PagesList = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const res = await axios.get(`${API}/cms/pages`, { params });
      setPages(res.data);
    } catch {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const createNew = async () => {
    const title = window.prompt("Page title (e.g. About Us, Privacy Policy):", "New Page");
    if (!title) return;
    try {
      const res = await axios.post(`${API}/cms/pages`, { title, description: "", sections: [] });
      nav(`/app/website/pages/${res.data.id}`);
    } catch {
      toast.error("Could not create page");
    }
  };

  const togglePublish = async (p) => {
    const ep = p.status === "published" ? "unpublish" : "publish";
    await axios.post(`${API}/cms/pages/${p.id}/${ep}`);
    toast.success(p.status === "published" ? "Unpublished" : "Published");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this page permanently?")) return;
    await axios.delete(`${API}/cms/pages/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pages</h1>
          <p className="text-sm text-slate-500">Create about, privacy, product detail, and any other page on your site.</p>
        </div>
        <button onClick={createNew} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-5 py-2.5 rounded-lg" data-testid="new-page-btn">
          <Plus size={16} /> New page
        </button>
      </header>

      <div className="flex gap-2">
        {[["All", ""], ["Drafts", "draft"], ["Published", "published"]].map(([lbl, val]) => (
          <button key={val} onClick={() => setFilter(val)} className={`text-sm px-4 py-1.5 rounded-full border transition ${filter === val ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{lbl}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : pages.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No pages yet. Click <b>New page</b> to create one.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">URL</th>
                <th className="text-left px-4 py-3 font-semibold">Updated</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`page-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/app/website/pages/${p.id}`} className="font-medium text-slate-900 hover:text-prepify-600 inline-flex items-center gap-2">
                      <FileText size={14} className="text-slate-400" /> {p.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-prepify-100 text-prepify-700" : "bg-slate-100 text-slate-600"}`}>
                      {p.status === "published" ? <Eye size={12} /> : <EyeOff size={12} />}
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">/{p.slug}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(p.updated_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {p.status === "published" && (
                        <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1"><ExternalLink size={12} /></a>
                      )}
                      <button onClick={() => togglePublish(p)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50">
                        {p.status === "published" ? "Unpublish" : "Publish"}
                      </button>
                      <Link to={`/app/website/pages/${p.id}`} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1"><Edit size={12} /></Link>
                      <button onClick={() => remove(p.id)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PagesList;
